// Everything is computed in SI units (metres, m/s, °C, mm); unit systems only affect inputs and labels.
export type UnitSystem = 'metric' | 'imperial';

export const UNIT_SYSTEM_IDS = ['metric', 'imperial'] as const satisfies readonly UnitSystem[];

export interface UnitSpec {
  symbol: string;
  /** Base units (metres, m/s, mm) per unit. */
  metres: number;
}

export interface UnitSystemSpec {
  height: UnitSpec;
  distance: UnitSpec;
  /** Wind speed; `metres` is m/s per unit. */
  speed: UnitSpec;
  /** Precipitation depth; `metres` is mm per unit. */
  precip: UnitSpec;
  temperature: { symbol: string; fromCelsius: (c: number) => number };
}

const FT = 0.3048;
const MI = 1609.344;

export const UNIT_SYSTEMS: Record<UnitSystem, UnitSystemSpec> = {
  metric: {
    height: { symbol: 'm', metres: 1 },
    distance: { symbol: 'km', metres: 1000 },
    speed: { symbol: 'km/h', metres: 1000 / 3600 },
    precip: { symbol: 'mm', metres: 1 },
    temperature: { symbol: '°C', fromCelsius: (c) => c },
  },
  imperial: {
    height: { symbol: 'ft', metres: FT },
    distance: { symbol: 'mi', metres: MI },
    speed: { symbol: 'mph', metres: MI / 3600 },
    precip: { symbol: 'in', metres: 25.4 },
    temperature: { symbol: '°F', fromCelsius: (c) => (c * 9) / 5 + 32 },
  },
};

export const defaultUnitSystem = (): UnitSystem =>
  typeof navigator !== 'undefined' && navigator.language === 'en-US' ? 'imperial' : 'metric';
